import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';

const normalize = (value) => (value ?? '').trim();

/**
 * Decide whether a UW's edit to the Details "Policy restrictions" section should be forwarded to
 * policy-mod as `uwReviewAnswers` when confirming a farm endorsement (#32405).
 *
 * The restriction is a form-only field (not part of the segment payload). When a UW changes it we
 * forward `{ restrictions }` so policy-mod stores it on the version-history entry — which rolls the
 * FMI 122 restriction form and prints on the endorsement documents.
 *
 * Returns the payload only when ALL hold, else undefined:
 *  - farm-based policy (Home / Rental / Farm),
 *  - the confirming user is an underwriter (`canApproveUWReview`),
 *  - the restriction actually changed (whitespace-insensitive; "" ⇒ removal),
 *  - the change is NOT already flowing through the FarmUWQuestionsModal (`isFarmUWReview`), which
 *    forwards restrictions itself.
 *
 * The forwarded value preserves the UW's raw text (only the change check is whitespace-insensitive).
 */
export const buildRestrictionUwReviewAnswers = ({
  policyType,
  canApproveUWReview,
  isFarmUWReview,
  currentRestriction,
  editedRestriction
}) => {
  if (!isFarmBasedPolicyType(policyType) || !canApproveUWReview || isFarmUWReview) {
    return undefined;
  }
  // `undefined` means the restriction field was never part of this edit session (e.g. a preview
  // reopened from a link that didn't flow through the Details form). Only a real edit seeds it to a
  // string ("" for a deliberate removal), so never treat `undefined` as a change — otherwise a
  // reopened-preview confirm would silently clear an existing restriction.
  if (editedRestriction === undefined) {
    return undefined;
  }
  if (normalize(editedRestriction) === normalize(currentRestriction)) {
    return undefined;
  }
  return { restrictions: editedRestriction };
};

/**
 * Capture a UW's edited restriction against the preview it was generated for (#32405).
 *
 * The restriction is a form-only field, so it never rides on the segment/preview payload — it has to
 * be carried separately and applied on confirm. We tag the captured value with the preview's id so
 * `resolveEditedRestriction` can apply it ONLY when that exact preview is confirmed. Keying by
 * previewId (rather than a bare value) is what lets the capture survive the preview→confirm
 * round-trip without a shared ref being clobbered when the preview store updates — the reopened-#32405
 * bug, where a cleared restriction never reached policy-mod (`uwReviewAnswers` arrived as null).
 */
export const captureEditedRestriction = (previewId, restrictions) => ({ previewId, restrictions });

/**
 * Resolve the captured restriction for the preview being confirmed. Returns the captured value
 * ("" for a deliberate removal) only when the capture belongs to `previewId`; otherwise undefined,
 * so a capture from an earlier in-session edit or a link-loaded preview is never forwarded.
 */
export const resolveEditedRestriction = (capture, previewId) =>
  capture && capture.previewId === previewId ? capture.restrictions : undefined;

export default buildRestrictionUwReviewAnswers;
